import { useRef, useState, DragEvent, ChangeEvent } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  Backdrop,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { read, utils } from 'xlsx';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import { HeaderCell } from 'src/containers/dashboard/garm/components';
import { SelectTableHeader } from './SelectTableHeader';
import Checkbox from 'src/components/Checkbox';
import useNotification from 'src/hooks/useNotification';

import { ReactComponent as DownloadIcon } from 'src/assets/icons/download.svg';
import { ReactComponent as UploadIcon } from 'src/assets/icons/uploadCloud.svg';
import CancelIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import { deleteGarmCustomField } from 'src/apis/garm';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 1000,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 16,
};

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      height: '100%',
    },
    importContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    dragDropImport: {
      flex: 1,
      position: 'relative',

      '& > input': {
        display: 'none',
      },

      '& > label': {
        display: 'flex',
        border: '1px dashed var(--gray-200, #EAECF0)',
        background: '#FFF',
        padding: '16px 24px',
        height: '100%',
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',

        '&.drag-active': {
          background: '#F7F7F7',
        },

        '& > div': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        },
      },

      '& #drag-file-element': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        borderRadius: 8,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    downloadBtn: {
      '& > span': {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      },
    },
    uploadIcon: {
      marginBottom: 8,
      width: 42,
      height: 42,
      borderRadius: 8,
      border: '1px solid var(--gray-200, #EAECF0)',
      padding: 10,
      background: '#FFF',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    importedData: {
      flex: 1,
      position: 'relative',
      border: '1px solid var(--gray-200, #EAECF0)',
      background: '#FFF',
      padding: '16px 24px',
      height: '100%',
      borderRadius: 8,
    },
    table: {
      flex: 1,
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      border: '1px solid #EAECF0',
      borderRadius: 8,
      background: '#fff',
      height: '100%',
      borderCollapse: 'collapse',
      overflow: 'auto',
      maxHeight: 308,

      '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
          borderBottom: '1px solid #EAECF0',

          '&:last-child': {
            borderBottom: 'none',
          },

          '& .MuiTableCell-root': {
            borderBottom: 'none',
            borderRight: '1px solid #EAECF0',

            '&:last-child': {
              borderRight: 'none',
            },
          },
        },
      },
    },
    noRSSFeed: {
      marginLeft: 4,
      '& span': {
        fontSize: 12,
        fontWeight: 'normal',
      },
      '& .MuiIconButton-label': {
        transform: 'scale(0.7)',
      },
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    confirmModalContent: {
      marginTop: 16,
      marginBottom: 24,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    confirmBtn: {
      flex: 1,
    },
  })
);

const FileTypes = ['csv', 'sheet', 'xls', 'xlsx'];

const CustomHeaderTypes = [
  { title: 'String', value: 'string' },
  { title: 'Number', value: 'number' },
  { title: 'Date', value: 'date' },
];

interface ImportStepProps {
  data: Record<string, string>[];
  setData: (data: Record<string, string>[]) => void;
  isNoRSSFeed: boolean;
  setIsNoRSSFeed: (isNoRSSFeed: boolean) => void;
  onHeaderMappingsChange?: (mappings: Record<string, string>) => void;
  customHeaders?: { id: number; name: string; type: string }[];
  onUpdateCustomHeaders?: (
    customHeaders: { id: number; name: string; type: string }[]
  ) => void;
  dashboardId: Nullable<string>;
}

export const ImportStep = ({
  data,
  setData,
  isNoRSSFeed,
  setIsNoRSSFeed,
  onHeaderMappingsChange,
  customHeaders = [],
  onUpdateCustomHeaders,
  dashboardId,
}: ImportStepProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const { handleError } = useNotification();

  const [dragActive, setDragActive] = useState<boolean>(false);
  const [selectedHeaders, setSelectedHeaders] = useState<
    Record<string, string>
  >({});
  const [customHeaderModal, setCustomHeaderModal] = useState<boolean>(false);
  const [newCustomHeader, setNewCustomHeader] = useState<string>('');
  const [newCustomHeaderType, setNewCustomHeaderType] = useState<string>('');
  const [editingHeader, setEditingHeader] = useState<{
    id: number;
    name: string;
    type: string;
  } | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);

  const handleFile = (file: File) => {
    if (
      file.size > 1048576 ||
      FileTypes.every((type) => !file.type.includes(type))
    ) {
      handleError({}, 'Please upload CSV or XLS file less than 1 MB.');
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        const wb = read(event?.target?.result);
        const sheets = wb.SheetNames;
        if (sheets.length) {
          const rows: any = utils.sheet_to_json(wb.Sheets[sheets[0]]);
          if (rows.length) {
            setData(rows);
            // Reset selected headers when new data is imported
            setSelectedHeaders({});
          } else {
            handleError({}, 'Please add data to your file.');
          }
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleDrag = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = process.env.PUBLIC_URL + '/TemplateImport.xlsx';
    link.download = 'TemplateImport.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddCustomHeader = () => {
    if (
      newCustomHeader.trim() &&
      newCustomHeaderType.trim() &&
      onUpdateCustomHeaders
    ) {
      // Find the lowest available id from 1, 2, 3
      const usedIds = customHeaders.map((h) => h.id);
      const availableIds = [1, 2, 3].filter((id) => !usedIds.includes(id));
      if (availableIds.length === 0) return; // All ids used, do not add
      const newId = availableIds[0];
      onUpdateCustomHeaders([
        ...customHeaders,
        {
          id: newId,
          name: newCustomHeader.trim(),
          type: newCustomHeaderType.trim(),
        },
      ]);
      setNewCustomHeader('');
      setNewCustomHeaderType('');
      setCustomHeaderModal(false);
    }
  };

  const handleOpenCustomHeaderModal = () => {
    setCustomHeaderModal(true);
    setEditMode(false);
    setEditingHeader(null);
    setNewCustomHeader('');
    setNewCustomHeaderType('');
  };

  const handleEditCustomHeader = (header: {
    id: number;
    name: string;
    type: string;
  }) => {
    setEditingHeader(header);
    setNewCustomHeader(header.name);
    setNewCustomHeaderType(header.type);
    setEditMode(true);
  };

  const handleDeleteCustomHeader = async (headerToDelete: {
    id: number;
    name: string;
    type: string;
  }) => {
    if (onUpdateCustomHeaders) {
      try {
        if (dashboardId) {
          await deleteGarmCustomField(dashboardId, headerToDelete.id);
        }
        const updatedHeaders = customHeaders.filter(
          (header) => header.id !== headerToDelete.id
        );
        onUpdateCustomHeaders(updatedHeaders);
      } catch (err) {
        handleError(err, 'Failed to delete custom header');
      }
    }
  };

  const handleUpdateCustomHeader = () => {
    if (
      newCustomHeader.trim() &&
      newCustomHeaderType.trim() &&
      onUpdateCustomHeaders &&
      editingHeader
    ) {
      const updatedHeaders = customHeaders.map((header) => {
        if (header.id === editingHeader.id) {
          return {
            id: header.id,
            name: newCustomHeader.trim(),
            type: newCustomHeaderType.trim(),
          };
        }
        return header;
      });
      onUpdateCustomHeaders(updatedHeaders);
      setNewCustomHeader('');
      setNewCustomHeaderType('');
      setEditingHeader(null);
      setEditMode(false);
      setCustomHeaderModal(false);
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.importContent}>
        <Typography className={textClasses.smBold} style={{ color: '#344054' }}>
          Import Show List and RSS Feeds
        </Typography>
        <Typography
          className={textClasses.xsRegular}
          style={{ color: '#667085' }}
        >
          Choose or drop an Excel or CSV file of all shows (maximum 100) you
          would like to import into your dashboard to be scored. Include the
          show&apos;s name as well as its RSS feed.
        </Typography>
        <Box className={classes.dragDropImport} onDragEnter={handleDrag}>
          <input
            ref={inputRef}
            type='file'
            id='input-file-upload'
            onChange={handleChange}
          />
          {data && data.length ? (
            <TableContainer component={Paper} className={classes.table}>
              <Table>
                <TableHead
                  style={{
                    height: 48,
                  }}
                >
                  <TableRow>
                    {Object.keys(data[0]).map((column: string) => {
                      // Get current header selection for this column
                      const currentSelection =
                        selectedHeaders[column] || column;

                      // Create available options based on current selection
                      let allOptions: string[];
                      if (selectedHeaders[column]) {
                        allOptions = [
                          'Show Title',
                          'RSS Feed',
                          ...customHeaders.map((ch) => ch.name),
                          column,
                        ];
                      } else {
                        allOptions = [
                          'Show Title',
                          'RSS Feed',
                          ...customHeaders.map((ch) => ch.name),
                        ];
                      }

                      const availableOptions = allOptions.filter((option) => {
                        if (option === currentSelection) return false;
                        return true;
                      });

                      return (
                        <HeaderCell
                          key={column}
                          style={{
                            padding: '4px 10px',
                            borderRadius: 0,
                            borderBottom: 'none',
                            background: '#F8F8F8',
                          }}
                        >
                          <SelectTableHeader
                            options={availableOptions}
                            header={currentSelection}
                            originalColumnName={column}
                            setHeader={(newHeader) => {
                              if (newHeader !== currentSelection) {
                                // Update selected headers state
                                let newSelectedHeaders = {
                                  ...selectedHeaders,
                                  [column]: newHeader,
                                };

                                // If selecting a standard option, reset other columns that have standard options
                                if (
                                  newHeader === 'Show Title' ||
                                  newHeader === 'RSS Feed' ||
                                  customHeaders.some(
                                    (ch) => ch.name === newHeader
                                  )
                                ) {
                                  newSelectedHeaders = Object.keys(
                                    newSelectedHeaders
                                  ).reduce((acc, col) => {
                                    const currentMapping =
                                      newSelectedHeaders[col];
                                    // Keep the new selection for current column, reset others to original
                                    if (col === column) {
                                      acc[col] = newHeader;
                                    } else if (currentMapping === newHeader) {
                                      // Reset other columns with standard options to their original names
                                      acc[col] = col;
                                    } else {
                                      // Keep other non-standard mappings
                                      acc[col] = currentMapping;
                                    }
                                    return acc;
                                  }, {} as Record<string, string>);
                                }

                                setSelectedHeaders(newSelectedHeaders);

                                // Notify parent of header mappings change
                                if (onHeaderMappingsChange) {
                                  onHeaderMappingsChange(newSelectedHeaders);
                                }
                              }
                            }}
                          />
                        </HeaderCell>
                      );
                    })}
                    <HeaderCell
                      align='center'
                      style={{
                        padding: '4px 10px',
                        borderRadius: 0,
                        borderBottom: 'none',
                        background: '#F8F8F8',
                      }}
                    ></HeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((item, index) => (
                    <TableRow
                      key={index}
                      style={{
                        height: '48px',
                      }}
                    >
                      {Object.keys(item).map((key) => (
                        <TableCell key={key}>
                          <Typography
                            className={textClasses.xsRegular}
                            style={{
                              color: '#475467',
                            }}
                          >
                            {item[key]}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <label
              id='label-file-upload'
              htmlFor='input-file-upload'
              className={dragActive ? 'drag-active' : ''}
            >
              <div>
                <div className={classes.uploadIcon}>
                  <UploadIcon width={20} height={20} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Button
                    variant='text'
                    fontSize='sm'
                    height={20}
                    onClick={handleUploadClick}
                  >
                    <Typography
                      className={textClasses.smBold}
                      style={{ color: '#5EA1F2' }}
                    >
                      Click to upload
                    </Typography>
                  </Button>
                  <Typography
                    className={textClasses.smRegular}
                    style={{ color: '#475467' }}
                  >
                    or drag and drop
                  </Typography>
                </div>
                <Typography
                  className={textClasses.xsRegular}
                  style={{ color: '#475467' }}
                >
                  CSV or XLS
                </Typography>
              </div>
            </label>
          )}
          {dragActive && (
            <div
              id='drag-file-element'
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            />
          )}
        </Box>
        <Typography
          className={textClasses.xsRegular}
          style={{ color: '#667085' }}
        >
          <span style={{ fontWeight: 500 }}>Required fields:</span> Show Title
          {isNoRSSFeed ? '' : ', RSS Feed'}
        </Typography>
        <Box className={classes.noRSSFeed}>
          <Checkbox
            name='No RSS Feed'
            label='No RSS Feed'
            checked={isNoRSSFeed}
            noPadding={true}
            onChange={setIsNoRSSFeed}
          />
        </Box>
      </Box>
      <Box style={{ display: 'flex', gap: 12 }}>
        <Button
          className={classes.downloadBtn}
          variant='outlined'
          fontSize='sm'
          height={38}
          onClick={downloadTemplate}
        >
          <DownloadIcon />
          <Typography
            className={textClasses.smBold}
            style={{ color: '#344054' }}
          >
            Download Template
          </Typography>
        </Button>
        <Button
          variant='outlined'
          fontSize='sm'
          height={38}
          onClick={handleOpenCustomHeaderModal}
          disabled={customHeaders.length > 3}
          style={
            customHeaders.length > 3
              ? { background: 'gray', cursor: 'not-allowed' }
              : {}
          }
        >
          <Typography
            className={textClasses.smBold}
            style={{ color: '#344054' }}
          >
            Add Custom Header
          </Typography>
        </Button>
      </Box>

      {/* Custom Header Modal */}
      <Modal
        open={customHeaderModal}
        onClose={() => setCustomHeaderModal(false)}
        aria-labelledby='custom-header-modal-title'
        aria-describedby='custom-header-modal-description'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Box sx={{ ...modalStyle, maxWidth: 400 }}>
          <Box className={classes.header}>
            <Typography
              className={textClasses.lgBold}
              style={{ color: '#101828' }}
            >
              {editMode ? 'Edit Custom Header' : 'Manage Custom Headers'}
            </Typography>
            <CancelIcon
              cursor='pointer'
              width={24}
              height={24}
              onClick={() => setCustomHeaderModal(false)}
            />
          </Box>
          <Box className={classes.confirmModalContent}>
            {/* Existing Custom Headers */}
            {customHeaders.length > 0 && (
              <Box style={{ marginBottom: 24 }}>
                <Typography
                  className={textClasses.smBold}
                  style={{ color: '#344054', marginBottom: 12 }}
                >
                  Existing Custom Headers
                </Typography>
                <Box style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {customHeaders.map((header) => (
                    <Box
                      key={header.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        border: '1px solid #EAECF0',
                        borderRadius: 8,
                        marginBottom: 8,
                        background: '#FAFAFA',
                      }}
                    >
                      <Box>
                        <Typography
                          className={textClasses.smRegular}
                          style={{ color: '#344054', fontWeight: 500 }}
                        >
                          {header.name}
                        </Typography>
                        <Typography
                          className={textClasses.xsRegular}
                          style={{ color: '#667085' }}
                        >
                          Type:{' '}
                          {CustomHeaderTypes.find(
                            (t) => t.value === header.type
                          )?.title || header.type}
                        </Typography>
                      </Box>
                      <Box style={{ display: 'flex', gap: 8 }}>
                        <IconButton
                          size='small'
                          onClick={() => handleEditCustomHeader(header)}
                          style={{ padding: 4 }}
                        >
                          <EditIcon
                            style={{ fontSize: 16, color: '#667085' }}
                          />
                        </IconButton>
                        <IconButton
                          size='small'
                          onClick={() => handleDeleteCustomHeader(header)}
                          style={{ padding: 4 }}
                        >
                          <DeleteIcon
                            style={{ fontSize: 16, color: '#DC2626' }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Add/Edit Form */}
            <Box>
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12,
                }}
              >
                <Typography
                  className={textClasses.smBold}
                  style={{ color: '#344054' }}
                >
                  {editMode ? 'Edit Custom Header' : 'Add New Custom Header'}
                </Typography>
                {editMode && (
                  <Button
                    variant='text'
                    fontSize='sm'
                    height={32}
                    onClick={() => {
                      setEditMode(false);
                      setEditingHeader(null);
                      setNewCustomHeader('');
                      setNewCustomHeaderType('');
                    }}
                    title='Add New'
                  />
                )}
              </Box>
              <TextField
                fullWidth
                label='Custom Header Name'
                value={newCustomHeader}
                onChange={(e) => setNewCustomHeader(e.target.value)}
                placeholder='Enter custom header name'
                style={{ marginBottom: 16 }}
              />
              <FormControl fullWidth style={{ marginBottom: 16 }}>
                <InputLabel>Custom Header Type</InputLabel>
                <Select
                  value={newCustomHeaderType}
                  onChange={(e) =>
                    setNewCustomHeaderType(e.target.value as string)
                  }
                  label='Custom Header Type'
                >
                  {CustomHeaderTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Button
              className={classes.confirmBtn}
              variant='outlined'
              height={46}
              onClick={() => {
                setCustomHeaderModal(false);
                setNewCustomHeader('');
                setNewCustomHeaderType('');
                setEditingHeader(null);
                setEditMode(false);
              }}
              title='Cancel'
            />
            <Button
              className={classes.confirmBtn}
              title={editMode ? 'Update Header' : 'Add Header'}
              height={46}
              onClick={
                editMode ? handleUpdateCustomHeader : handleAddCustomHeader
              }
              disabled={!newCustomHeader.trim() || !newCustomHeaderType.trim()}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
